import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { downloadFile, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { EStatementType } from "types";

import { refreshIntegrationsOnGoogleReauthRequired, toastGoogleBackedError } from "./integrationsStore";
import type { MainPortalType } from "./settingsStore";
import type { ILastStatement } from "./types";

export const fetchStatement = createAsyncThunk(
    "statements/channels",
    async ({ termId, periodId }: { termId: number; periodId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement", {
                termId,
                periodId,
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchNetStatement = createAsyncThunk(
    "netStatements/channels",
    async (
        {
            termId,
            periodId,
            companyId,
            articleId,
            isBeneficiaryPortal = false,
        }: {
            termId?: number;
            periodId?: number;
            companyId?: number;
            articleId?: number;
            isBeneficiaryPortal?: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                (isBeneficiaryPortal
                    ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
                    : import.meta.env.REACT_APP_API_ENDPOINT) + "/statement",
                {
                    termId,
                    periodId,
                    companyId,
                    articleId,
                    statementType: "net",
                },
            );
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchPrecountStatement = createAsyncThunk(
    "precountStatements/channels",
    async (
        {
            termId,
            periodId,
            companyId,
            articleId,
            isBeneficiaryPortal = false,
        }: {
            termId?: number;
            periodId?: number;
            companyId?: number;
            articleId?: number;
            isBeneficiaryPortal?: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                (isBeneficiaryPortal
                    ? import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT
                    : import.meta.env.REACT_APP_API_ENDPOINT) + "/statement",
                {
                    termId,
                    periodId,
                    companyId,
                    articleId,
                    statementType: "precount",
                },
            );
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const resetStatements = createAsyncThunk("resetStatements", async () => "");

export const fetchBeneficiaryStatement = createAsyncThunk(
    "statements/beneficiary",
    async (
        { beneficiaryId, periodId, companyId }: { beneficiaryId: number; periodId: number; companyId?: number },
        { rejectWithValue, dispatch },
    ) => {
        const isBeneficiarPortal = !!companyId;
        const params = isBeneficiarPortal
            ? { beneficiarId: beneficiaryId, periodId, companyId }
            : { beneficiarId: beneficiaryId, periodId };
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar",
                params,
            );
            return response.data.document;
        } catch (error: any) {
            refreshIntegrationsOnGoogleReauthRequired(error, dispatch);
            return rejectWithValue(error.response.data);
        }
    },
);

export const sendBeneficiaryMergedStatements = createAsyncThunk(
    "statements/sendBeneficiaryMergedStatements",
    async (
        {
            beneficiaryId,
            periodId,
            sendToBeneficiary,
            mailCc,
            grossStatementsOnly,
            subject,
            body,
        }: {
            beneficiaryId: number;
            periodId: number;
            sendToBeneficiary?: boolean;
            mailCc?: string[];
            grossStatementsOnly?: boolean;
            subject?: string;
            body?: string;
        },
        { rejectWithValue },
    ) => {
        const params = {
            beneficiaryId,
            periodId,
            sendToBeneficiary: !!sendToBeneficiary,
            mailCc,
            grossStatementsOnly,
            subject,
            body,
        };

        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/statement/merged/send",
                params,
            );

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiaryNetStatement = createAsyncThunk(
    "statements/beneficiaryNet",
    async (
        { beneficiaryId, periodId, companyId }: { beneficiaryId: number; periodId: number; companyId?: number },
        { rejectWithValue },
    ) => {
        const isBeneficiarPortal = !!companyId;
        const params = isBeneficiarPortal
            ? { beneficiarId: beneficiaryId, periodId, companyId }
            : { beneficiarId: beneficiaryId, periodId };
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar", {
                ...params,
                statementType: "net",
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBeneficiaryPrecountStatement = createAsyncThunk(
    "statements/beneficiaryPrecount",
    async (
        { beneficiaryId, periodId, companyId }: { beneficiaryId: number; periodId: number; companyId?: number },
        { rejectWithValue },
    ) => {
        const isBeneficiarPortal = !!companyId;
        const params = isBeneficiarPortal
            ? { beneficiarId: beneficiaryId, periodId, companyId }
            : { beneficiarId: beneficiaryId, periodId };
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar", {
                ...params,
                statementType: "precount",
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAndSaveStatement = createAsyncThunk(
    "statements/channelsToSave",
    async ({
        termId,
        beneficiarId,
        articleId,
        periodId,
        fileName = "statement.pdf",
        statementType = "gross",
        companyId,
        type = "book",
    }: {
        termId?: number | null;
        beneficiarId?: number;
        articleId?: number;
        periodId?: number;
        fileName: string;
        statementType?: string;
        companyId?: number;
        type?: MainPortalType;
    }) => {
        const companyParam = companyId ? { companyId } : {};
        const params =
            termId && !beneficiarId
                ? { termId, periodId, statementType, ...companyParam }
                : !termId && beneficiarId
                  ? { beneficiarId, periodId, statementType, ...companyParam }
                  : null;
        if (params == null && type != "press") return;
        const route = termId
            ? import.meta.env.REACT_APP_API_ENDPOINT + "/statement/generate"
            : import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/generate";

        const response = await toast.promise(
            axios.post(
                ["audiovisual", "press"].includes(type)
                    ? import.meta.env.REACT_APP_API_ENDPOINT + "/statement/generate"
                    : route,
                ["audiovisual", "press"].includes(type) ? { articleId, statementType, ...companyParam } : params,
            ),
            {
                pending: `${t("ShowStatements.PendingDownload")}`,
                success: `${t("ShowStatements.SuccessDownload")}`,
                error: `${t("ShowStatements.ErrorDownload")}`,
            },
        );
        const linkSource = `data:application/pdf;base64,${response.data}`;
        downloadFile({ source: linkSource, fileName, isLink: true });

        return response.data.document;
    },
);

export const fetchAndSaveBeneficiarStatement = createAsyncThunk(
    "statements/channelsToSaveBeneficiar",
    async ({
        beneficiarId,
        periodId,
        fileName = "statement.pdf",
        statementType = "gross",
    }: {
        beneficiarId?: number;
        periodId: number;
        fileName: string;
        statementType?: string;
    }) => {
        const params = { beneficiarId, periodId, statementType };
        if (params == null) return;
        const route = import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/generate";
        const response = await toast.promise(axios.post(route, params), {
            pending: `${t("ShowStatements.PendingDownload")}`,
            success: `${t("ShowStatements.SuccessDownload")}`,
            error: `${t("ShowStatements.ErrorDownload")}`,
        });
        const linkSource = `data:application/pdf;base64,${response.data}`;
        downloadFile({ source: linkSource, fileName, isLink: true });
        return response.data.document;
    },
);

export const fetchAndSaveAdvanceStatement = createAsyncThunk(
    "statements/fetchAndSaveAdvanceStatement",
    async ({
        termId,
        beneficiarId,
        advancePaymentId,
        advancePaymentSliceId,
        fileName = "statement.pdf",
        statementType = "gross",
        companyId,
    }: {
        termId?: number;
        beneficiarId?: number;
        advancePaymentId: number;
        advancePaymentSliceId: number;
        fileName: string;
        statementType?: string;
        companyId?: number;
    }) => {
        const companyParam = companyId ? { companyId } : {};
        const params =
            termId && !beneficiarId
                ? {
                      termId,
                      statementType,
                      advancePaymentId,
                      advancePaymentSliceId,
                      ...companyParam,
                  }
                : !termId && beneficiarId
                  ? { beneficiarId }
                  : null;

        if (params == null) return;
        const route = termId
            ? import.meta.env.REACT_APP_API_ENDPOINT + "/statement/generate"
            : import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/generate";
        const response = await toast.promise(axios.post(route, params), {
            pending: `${t("ShowStatements.PendingDownload")}`,
            success: `${t("ShowStatements.SuccessDownload")}`,
            error: `${t("ShowStatements.ErrorDownload")}`,
        });
        const linkSource = `data:application/pdf;base64,${response.data}`;
        downloadFile({ source: linkSource, fileName, isLink: true });
        return response.data.document;
    },
);

export const sendMailWithStatement = createAsyncThunk(
    "statements/channelsToEmail",
    async ({
        termId,
        periodId,
        articleId,
        mailCc,
        subject,
        body,
        ignore,
    }: {
        termId?: number;
        periodId?: number;
        articleId?: number;
        mailCc: string[];
        subject: string;
        body: string;
        ignore?: EStatementType[];
    }) => {
        const response = await toast.promise(
            axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/send", {
                termId,
                periodId,
                articleId,
                mailCc,
                subject,
                body,
                ignore,
            }),
            {
                pending: `${t("SendEmail.Pending")}`,
                success: `${t("SendEmail.Successful")}`,
                error: `${t("SendEmail.Error")}`,
            },
        );

        return response.data.document;
    },
);

export const sendBulkMailWithStatement = createAsyncThunk(
    "statements/sendBulkMailWithStatement",
    async ({
        termId,
        articleId,
        periodId,
        mailCc,
        subject,
        body,
        subjectByLang,
        bodyByLang,
        ignore,
    }: {
        termId?: number[];
        articleId?: number[];
        periodId?: number;
        mailCc: string[];
        subject: string;
        body: string;
        subjectByLang?: Record<string, string>;
        bodyByLang?: Record<string, string>;
        ignore?: EStatementType[];
    }) => {
        const response = await toast.promise(
            axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/send/bulk", {
                termId,
                articleId,
                periodId,
                mailCc,
                subject,
                body,
                subjectByLang,
                bodyByLang,
                ignore,
            }),
            {
                pending: `${t("SendEmail.Pending")}`,
                error: `${t("SendEmail.Error")}`,
            },
        );
        return { ids: (termId || articleId || []).length };
    },
);

export const sendMailWithBeneficiarStatement = createAsyncThunk(
    "statements/channelsToEmail",
    async ({
        beneficiarId,
        periodId,
        mailCc,
        subject,
        body,
        ignore,
    }: {
        beneficiarId: number;
        periodId: number;
        mailCc: string[];
        subject: string;
        body: string;
        ignore?: EStatementType[];
    }) => {
        const response = await toast.promise(
            axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/send", {
                beneficiarId,
                periodId,
                mailCc,
                subject,
                body,
                ignore,
            }),
            {
                pending: `${t("SendEmail.Pending")}`,
                success: `${t("SendEmail.Successful")}`,
                error: `${t("SendEmail.Error")}`,
            },
        );

        return response.data.document;
    },
);

export const sendBulkMailWithBeneficiarStatement = createAsyncThunk(
    "statements/sendBulkMailWithBeneficiarStatement",
    async ({
        beneficiarId,
        periodId,
        mailCc,
        subject,
        body,
        subjectByLang,
        bodyByLang,
        ignore,
    }: {
        beneficiarId: number[];
        periodId: number;
        mailCc: string[];
        subject: string;
        body: string;
        subjectByLang?: Record<string, string>;
        bodyByLang?: Record<string, string>;
        ignore?: EStatementType[];
    }) => {
        const response = await toast.promise(
            axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/send/bulk", {
                beneficiarId,
                periodId,
                mailCc,
                subject,
                body,
                subjectByLang,
                bodyByLang,
                ignore,
            }),
            {
                pending: `${t("SendEmail.Pending")}`,
                success: `${t("SendEmail.Successful")}`,
                error: `${t("SendEmail.Error")}`,
            },
        );

        return response.data.document;
    },
);

export const fetchBeneficiarPortalStatement = createAsyncThunk(
    "statements/fetchBeneficiarPortalStatement",
    async (
        { termId, periodId, companyId }: { termId: number; periodId: number; companyId: number },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement", {
                termId,
                periodId,
                companyId,
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchCollectiveManagementStatement = createAsyncThunk(
    "statements/fetchCollectiveManagementStatement",
    async (
        {
            termId,
            periodId,
            companyId,
        }: {
            termId: number;
            periodId: number;
            companyId?: number;
        },
        { rejectWithValue },
    ) => {
        const companyParam = companyId ? { companyId } : {};
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement", {
                termId,
                periodId,
                statementType: "collectiveManagement",
                ...companyParam,
            });
            return response.data.document;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchAdvancePaymentsStatement = createAsyncThunk(
    "statements/fetchAdvancePaymentsStatement",
    async (
        {
            termId,
            advancePaymentId,
            advancePaymentSliceId,
            statementType,
            companyId,
        }: {
            termId: number;
            advancePaymentId: number;
            advancePaymentSliceId: number;
            statementType: "net" | "precount";
            companyId?: number;
        },
        { rejectWithValue },
    ) => {
        const companyParam = companyId ? { companyId } : {};
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement", {
                termId,
                advancePaymentId,
                advancePaymentSliceId,
                statementType,
                ...companyParam,
            });
            return { statement: response.data.document, statementType };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const downloadStatementArchive = createAsyncThunk(
    "statements/downloadStatementArchive",
    async ({ query }: { query: string | null }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/statement/download?fileKey=${query}`, {
                    headers: {
                        Accept: "*/*",
                    },
                    responseType: "blob",
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `archive-${query}.zip`;
            downloadFile({ source: response.data, fileName });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchLastStatements = createAsyncThunk(
    "beneficiarPortal/fetchLastStatements",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/statements/latest`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBPStatementsByTitle = createAsyncThunk(
    "beneficiarPortal/fetchBPStatementsByTitle",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/statements`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBPStatementsByBeneficiars = createAsyncThunk(
    "beneficiarPortal/fetchBPStatementsByBeneficiars",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/statements`, {
                params: { type: "consolidated" },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchBPStatementsByArticles = createAsyncThunk(
    "beneficiarPortal/fetchBPStatementsByArticles",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_BENEFICIAR_PORTAL_ENDPOINT + `/statements`, {
                params: { type: "article" },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchContract = createAsyncThunk(
    "press/fetchContract",
    async (
        {
            titleId,
            articleId,
            companyId,
            from = "press",
            download,
        }: {
            titleId: number;
            articleId: number;
            companyId?: number;
            from?: "press" | "book" | "audiovisual";
            download?: boolean;
        },
        { rejectWithValue },
    ) => {
        const link = ["audiovisual", "press"].includes(from)
            ? `/title/${titleId}/article/${articleId}/agreement`
            : `/term/${articleId}/agreement`;
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + link, {
                params: { companyId, download },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchContractDownload = createAsyncThunk(
    "press/fetchContractDownload",
    async ({
        titleId,
        articleId,
        download,
        filename,
        companyId,
        from = "press",
        userVariables,
        templateId,
    }: {
        titleId: number;
        articleId: number;
        download: boolean;
        filename: string;
        companyId?: number;
        from?: "press" | "book" | "audiovisual";
        userVariables?: any;
        templateId?: number;
    }) => {
        const link = ["audiovisual", "press"].includes(from)
            ? `/title/${titleId}/article/${articleId}/agreement`
            : `/term/${articleId}/agreement`;
        const response = await toast.promise(
            axios.get(import.meta.env.REACT_APP_API_ENDPOINT + link, {
                params: { download, templateId, companyId, ...userVariables },
            }),
            {
                pending: `${t("Articles.ContractPending")}`,
                success: `${t("Articles.ContractSucceeded")}`,
                error: `${t("Articles.ContractFailed")}`,
            },
        );
        const linkSource = `data:application/pdf;base64,${response.data}`;
        downloadFile({ source: linkSource, fileName: filename, isLink: true });
        return null;
    },
);

//TODO: implement three or more statements
export interface StatementsState {
    statement: string;
    netStatement: string;
    precountStatement: string;
    collectiveManagementStatement: string;
    statementByBeneficiary: string;
    netStatementByBeneficiary: string;
    precountStatementByBeneficiary: string;
    beneficiarPortalStatement: string;
    advancePaymentNetStatement: string;
    advancePaymentPrecountStatement: string;
    contract: string;
    status: "idle" | "pending" | "succeeded" | "failed";
    savingStatus: "idle" | "pending" | "succeeded" | "failed";
    lastStatements: ILastStatement[];
    bpStatementsByTitle: ILastStatement[];
    bpStatementsByArticles: ILastStatement[];
    bpStatementsByBeneficiars: ILastStatement[];
    downloadStatus: "idle" | "pending" | "succeeded" | "failed";
}

const initialState: StatementsState = {
    statement: "",
    netStatement: "",
    precountStatement: "",
    collectiveManagementStatement: "",
    statementByBeneficiary: "",
    netStatementByBeneficiary: "",
    precountStatementByBeneficiary: "",
    beneficiarPortalStatement: "",
    advancePaymentNetStatement: "",
    advancePaymentPrecountStatement: "",
    contract: "",
    status: "idle",
    savingStatus: "idle",
    lastStatements: [],
    bpStatementsByTitle: [],
    bpStatementsByArticles: [],
    bpStatementsByBeneficiars: [],
    downloadStatus: "idle",
};

export const statementsSlice = createSlice({
    name: "settings",
    initialState,
    reducers: {
        setStatementByBeneficiary(state, action) {
            state.statementByBeneficiary = action.payload;
        },
        resetStatement(state) {
            state.statement = "";
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchStatement.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.statement = action.payload;
        });
        builder.addCase(fetchStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!(payload as any)?.message.includes("mutliple price per channel")) {
                // the typo isn't mine :)
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(fetchStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchNetStatement.fulfilled, (state, action) => {
            state.netStatement = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchNetStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!(payload as any)?.message.includes("mutliple price per channel")) {
                // the typo isn't mine :)
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(fetchNetStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchPrecountStatement.fulfilled, (state, action) => {
            state.precountStatement = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchPrecountStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            if (!(payload as any)?.message.includes("mutliple price per channel")) {
                // the typo isn't mine :)
                toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            }
        });
        builder.addCase(fetchPrecountStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchAndSaveStatement.fulfilled, (state, action) => {
            state.savingStatus = "succeeded";
        });
        builder.addCase(fetchAndSaveStatement.rejected, (state, action) => {
            state.savingStatus = "failed";
        });
        builder.addCase(fetchAndSaveStatement.pending, (state, action) => {
            state.savingStatus = "pending";
        });

        builder.addCase(fetchAndSaveBeneficiarStatement.fulfilled, (state, action) => {
            state.savingStatus = "succeeded";
        });
        builder.addCase(fetchAndSaveBeneficiarStatement.rejected, (state, action) => {
            state.savingStatus = "failed";
        });
        builder.addCase(fetchAndSaveBeneficiarStatement.pending, (state, action) => {
            state.savingStatus = "pending";
        });

        builder.addCase(fetchAndSaveAdvanceStatement.fulfilled, (state, action) => {
            state.savingStatus = "succeeded";
        });
        builder.addCase(fetchAndSaveAdvanceStatement.rejected, (state, action) => {
            state.savingStatus = "failed";
        });
        builder.addCase(fetchAndSaveAdvanceStatement.pending, (state, action) => {
            state.savingStatus = "pending";
        });

        builder.addCase(sendBeneficiaryMergedStatements.fulfilled, (state, action) => {
            state.savingStatus = "succeeded";
            toast.success(`${t("ShowStatements.DownloadStartedToast")}`);
        });
        builder.addCase(sendBeneficiaryMergedStatements.rejected, (state, action) => {
            state.savingStatus = "failed";
            toast.error(`${t("ShowStatements.DownloadErrorToast")}`);
        });
        builder.addCase(sendBeneficiaryMergedStatements.pending, (state, action) => {
            state.savingStatus = "pending";
        });

        builder.addCase(sendBulkMailWithStatement.fulfilled, (state, action) => {
            toast.success(`${t("Articles.SendMailSucceeded", { count: action.payload?.ids })}`);
        });

        builder.addCase(fetchBeneficiaryStatement.fulfilled, (state, action) => {
            state.statementByBeneficiary = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchBeneficiaryStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            toastGoogleBackedError(payload);
        });
        builder.addCase(fetchBeneficiaryStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchBeneficiaryNetStatement.fulfilled, (state, action) => {
            state.netStatementByBeneficiary = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchBeneficiaryNetStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchBeneficiaryNetStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchBeneficiaryPrecountStatement.fulfilled, (state, action) => {
            state.precountStatementByBeneficiary = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchBeneficiaryPrecountStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchBeneficiaryPrecountStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchBeneficiarPortalStatement.fulfilled, (state, action) => {
            state.beneficiarPortalStatement = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchBeneficiarPortalStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchBeneficiarPortalStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchCollectiveManagementStatement.fulfilled, (state, action) => {
            state.collectiveManagementStatement = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchCollectiveManagementStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchCollectiveManagementStatement.pending, (state) => {
            state.status = "pending";
        });

        builder.addCase(fetchAdvancePaymentsStatement.fulfilled, (state, action) => {
            if (action.payload.statementType == "net") state.advancePaymentNetStatement = action.payload.statement;
            else if (action.payload.statementType == "precount")
                state.advancePaymentPrecountStatement = action.payload.statement;
            state.status = "succeeded";
        });
        builder.addCase(fetchAdvancePaymentsStatement.rejected, (state, { payload }) => {
            state.status = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAdvancePaymentsStatement.pending, (state, action) => {
            state.status = "pending";
        });

        builder.addCase(fetchLastStatements.fulfilled, (state, action) => {
            // The response body has been seen without the expected list in production
            state.lastStatements = action.payload?.result ?? [];
        });
        builder.addCase(fetchLastStatements.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchBPStatementsByTitle.fulfilled, (state, action) => {
            state.bpStatementsByTitle = action.payload.result;
        });
        builder.addCase(fetchBPStatementsByTitle.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchBPStatementsByBeneficiars.fulfilled, (state, action) => {
            state.bpStatementsByBeneficiars = action.payload.result;
        });
        builder.addCase(fetchBPStatementsByBeneficiars.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchBPStatementsByArticles.fulfilled, (state, action) => {
            state.bpStatementsByArticles = action.payload.result;
        });
        builder.addCase(fetchBPStatementsByArticles.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchContract.fulfilled, (state, action) => {
            state.contract = action.payload;
            state.status = "succeeded";
        });
        builder.addCase(fetchContract.pending, (state, action) => {
            state.status = "pending";
        });
        builder.addCase(fetchContract.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.status = "failed";
        });

        builder.addCase(fetchContractDownload.fulfilled, (state, action) => {
            state.savingStatus = "succeeded";
        });
        builder.addCase(fetchContractDownload.pending, (state, action) => {
            state.savingStatus = "pending";
        });
        builder.addCase(fetchContractDownload.rejected, (state, action) => {
            state.savingStatus = "failed";
        });
        builder.addCase(resetStatements.fulfilled, (state, action) => {
            state.statement = action.payload;
            state.netStatement = action.payload;
            state.precountStatement = action.payload;
            state.collectiveManagementStatement = "";
            state.advancePaymentNetStatement = "";
            state.advancePaymentPrecountStatement = "";
        });
        builder.addCase(downloadStatementArchive.pending, (state) => {
            state.downloadStatus = "pending";
        });
        builder.addCase(downloadStatementArchive.fulfilled, (state) => {
            state.downloadStatus = "succeeded";
        });
    },
});

export default statementsSlice.reducer;
export const { setStatementByBeneficiary, resetStatement } = statementsSlice.actions;
